import React from 'react';
import { AlertCircle, Zap, CheckCircle, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { CopyIssue } from '@/types/copyAnalysis';
import { motion } from 'framer-motion';

interface IssueCardProps {
  issue: CopyIssue;
  screenshot?: string;
  onImageClick: (image: string) => void;
  onCopy: (text: string, id: number) => void;
  copiedId: number | null;
}

export function IssueCard({ 
  issue, 
  screenshot, 
  onImageClick, 
  onCopy, 
  copiedId 
}: IssueCardProps) {
  const { quoted, rest } = extractQuotedText(issue.solution);
  
  return (
    <motion.div 
      key={issue.id} 
      className="bg-white border border-gray-200 rounded-[24px] overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="grid grid-cols-3 divide-x divide-gray-200">
        <div className="p-8 space-y-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <span className="text-base font-medium">Issue №{issue.id}</span>
          </div>
          <div>
            <span className="text-sm uppercase tracking-wider text-gray-500 font-medium">
              {issue.category}
            </span>
            <h3 className="text-2xl font-bold text-gray-900 mt-2">{issue.title}</h3>
          </div>
          <div className="bg-gray-50 p-6 rounded-xl">
            <p className="text-sm text-gray-500 mb-2 font-medium">How it's now:</p>
            <p className="text-xl font-bold text-gray-900">{issue.currentState}</p>
            {screenshot && (
              <button
                onClick={() => onImageClick(screenshot)}
                className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ImageIcon className="w-4 h-4" />
                View screenshot
              </button>
            )}
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <span className="text-base font-medium">Importance of this</span>
          </div>
          <p className="text-lg leading-relaxed text-gray-600">{issue.importance}</p>
        </div>

        <div className="p-8 space-y-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
            <span className="text-base font-medium">Your solution</span>
          </div>
          <div className="space-y-4">
            <p className="text-2xl text-gray-900">
              {rest && <span>{rest} </span>}
              <span className="font-bold">"{quoted}"</span>
            </p>
            <button
              onClick={() => onCopy(quoted || issue.solution, issue.id)}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              {copiedId === issue.id ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copiedId === issue.id ? 'Copied!' : 'Copy solution'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function extractQuotedText(solution: string) {
  // Try to find text in quotes
  const matches = solution.match(/"([^"]+)"/);
  
  if (matches) {
    // If found, split into quoted part and rest
    const quoted = matches[1];
    const rest = solution
      .replace(/"([^"]+)"/, '') // Remove the quoted part
      .replace(/(?:change to|use|replace with|update to|modify to|new version:|suggested:|recommendation:)/i, '') // Remove common prefixes
      .trim();
    
    return { quoted, rest };
  }
  
  // If no quotes found, treat the entire solution as quoted
  return {
    quoted: solution.trim(),
    rest: ''
  };
}